import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GENERAL_ATTACHMENT_ACCEPT,
  GENERAL_ATTACHMENT_FORMATS_LABEL,
  GENERAL_ATTACHMENT_SUMMARY_LABEL,
  isAllowedGeneralAttachmentFile,
  type ActivityDocumentResponse,
} from '@packages/types';
import {
  uploadActivityDocument,
  listActivityDocuments,
  deleteActivityDocument,
  downloadActivityDocument,
} from '@packages/api/endpoints/activity-document.js';
import { queryKeys } from '@packages/api';
import { useAuth } from '../../lib/auth/useAuth';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useToast } from '../ui/Toast';
import { Reveal } from '../ui/Reveal';
import { Skeleton, SkeletonRegion } from '../ui/Skeleton';
import { useRevealGroup } from '../../hooks/useRevealGroup';
import { ReadError } from '../ui/ReadError';
import { formatBytes } from '@packages/format';
import { MimeTypeIcon } from '../ui/MimeTypeIcon';
import { TextButton } from '../ui/TextLink';
import { SupportingDocumentRow } from '../ui/SupportingDocumentRow';
import { RemoveDocumentDialog } from '../documents/RemoveDocumentDialog';
import { PaperclipIcon } from '../ui/PaperclipIcon';
import { UploadCloudIcon } from '../ui/UploadCloudIcon';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_ATTACHMENTS = 5;

// ---------------------------------------------------------------------------
// Icons (upload-specific; MIME display icons come from MimeTypeIcon)
// ---------------------------------------------------------------------------

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Pending file row (create mode)
// ---------------------------------------------------------------------------

interface PendingFile {
  id: string;
  file: File;
}

interface PendingFileRowProps {
  pending: PendingFile;
  onRemove: (id: string) => void;
}

function PendingFileRow({ pending, onRemove }: PendingFileRowProps) {
  return (
    <li className="flex items-center gap-2 rounded-md border border-border bg-bg-muted px-3 py-2 text-sm">
      <span className="shrink-0 text-text-muted">
        <MimeTypeIcon mimeType={pending.file.type} className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate text-secondary">{pending.file.name}</span>
      <span className="shrink-0 text-xs text-text-muted">{formatBytes(pending.file.size)}</span>
      <button
        type="button"
        onClick={() => onRemove(pending.id)}
        className="shrink-0 text-text-muted hover:text-error focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        aria-label={`Remove ${pending.file.name}`}
      >
        <TrashIcon className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ActivityAttachmentUploaderProps {
  /** Account ID - required in both modes */
  accountId: string;
  /**
   * Edit mode: activityId is provided, uploads/deletes happen immediately.
   * Create mode: no activityId, files are queued and returned via onPendingFilesChange.
   */
  activityId?: string;
  /**
   * Create mode only - called whenever the queued file list changes.
   * The parent should call uploadActivityDocument for each file after activity creation.
   */
  onPendingFilesChange?: (files: File[]) => void;
  /** Whether any uploads are in progress (used to disable the zone externally) */
  disabled?: boolean;
  /**
   * View-only mode: render the attached files so they can still be opened, but
   * offer no way to add or remove them. Used when the host form is read-only
   * (an invoiced activity, or a colleague's entry the viewer may not edit).
   * Listing and downloading are permitted for any member of the account, so
   * this shows real files rather than an empty shell - see the backend's
   * `verifyClientProjectActivityVisibility`, which gates both routes.
   */
  readOnly?: boolean;
}

export function ActivityAttachmentUploader({
  accountId,
  activityId,
  onPendingFilesChange,
  disabled = false,
  readOnly = false,
}: ActivityAttachmentUploaderProps) {
  const isEditMode = !!activityId;
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { accountId: authAccountId } = useAuth();
  const resolvedAccountId = accountId || authAccountId || '';

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create mode: local queue of pending files
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  // Notify the parent of queue changes from an effect, never from inside the
  // setState updater - calling a parent setState while this component renders
  // is a React setstate-in-render violation. The ref keeps the latest callback
  // reachable without re-firing the effect when the parent passes an inline
  // arrow (which changes identity every render).
  const onPendingFilesChangeRef = useRef(onPendingFilesChange);
  onPendingFilesChangeRef.current = onPendingFilesChange;
  useEffect(() => {
    if (!isEditMode) onPendingFilesChangeRef.current?.(pendingFiles.map((p) => p.file));
  }, [pendingFiles, isEditMode]);

  // Edit mode: fetch existing attachments.
  const {
    data: existingAttachments,
    isLoading: isLoadingAttachments,
    isError: isAttachmentsError,
    refetch: refetchAttachments,
  } = useQuery({
    queryKey: queryKeys.activityDocuments(resolvedAccountId, activityId ?? ''),
    queryFn: () => listActivityDocuments(resolvedAccountId, activityId!),
    enabled: isEditMode,
    staleTime: 30_000,
  });

  const attachmentsRead = { isLoading: isLoadingAttachments };
  const reveal = useRevealGroup([attachmentsRead]);

  const [pendingRemoval, setPendingRemoval] = useState<ActivityDocumentResponse | null>(null);

  // The removed row (and the cross that opened the dialog) unmounts when the
  // refetch lands, after the dialog has already handed focus back, so focus is
  // re-homed in the list effect below once the row is actually gone.
  const headerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const removedRef = useRef<{ id: string; index: number } | null>(null);

  useEffect(() => {
    const removed = removedRef.current;
    if (!removed || !existingAttachments) return;
    if (existingAttachments.some((a) => a.id === removed.id)) return;
    removedRef.current = null;

    const active = globalThis.document.activeElement;
    if (active && active !== globalThis.document.body) return;

    const removeButtons = listRef.current?.querySelectorAll<HTMLElement>('button[aria-label^="Remove "]');
    const target =
      removeButtons?.[Math.min(removed.index, removeButtons.length - 1)] ??
      headerRef.current?.querySelector<HTMLElement>('button');
    target?.focus();
  }, [existingAttachments]);

  // Edit mode: upload mutation - fires immediately on file drop
  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadActivityDocument(resolvedAccountId, activityId!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.activityDocuments(resolvedAccountId, activityId!),
      });
      // Invalidate the activity list so attachmentCount badge updates
      queryClient.invalidateQueries({
        queryKey: queryKeys.clientProjectActivities(resolvedAccountId),
      });
    },
    onError: (err) => {
      addToast({
        variant: 'error',
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    },
  });

  const currentCount = isEditMode
    ? (existingAttachments?.length ?? 0)
    : pendingFiles.length;

  function validateFile(file: File, currentTotal: number): string | null {
    if (!isAllowedGeneralAttachmentFile(file)) {
      return `"${file.name}" is not a supported file type. Allowed: ${GENERAL_ATTACHMENT_FORMATS_LABEL}.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `"${file.name}" exceeds the 10 MB limit (${formatBytes(file.size)}).`;
    }
    if (currentTotal >= MAX_ATTACHMENTS) {
      return `Maximum ${MAX_ATTACHMENTS} attachments allowed.`;
    }
    return null;
  }

  async function processFiles(files: File[]): Promise<void> {
    if (disabled) return;

    let runningCount = currentCount;

    for (const file of files) {
      const error = validateFile(file, runningCount);
      if (error) {
        addToast({ variant: 'error', title: 'File not accepted', description: error });
        continue;
      }

      if (isEditMode) {
        // Edit mode: upload immediately
        uploadMutation.mutate(file);
        runningCount += 1;
      } else {
        // Create mode: queue locally. The parent is notified by the effect
        // above once the state commit lands.
        const id = crypto.randomUUID();
        setPendingFiles((prev) => [...prev, { id, file }]);
        runningCount += 1;
      }
    }
  }

  function removePending(id: string) {
    setPendingFiles((prev) => prev.filter((p) => p.id !== id));
  }

  function refreshAttachments() {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.activityDocuments(resolvedAccountId, activityId!),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.clientProjectActivities(resolvedAccountId),
    });
  }

  async function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    await processFiles(Array.from(e.target.files ?? []));
    // Reset so the same file can be re-selected after removal
    e.target.value = '';
  }

  const { isDragging, onDragEnter, onDragOver, onDragLeave, onDrop } = useDragAndDrop(processFiles);

  const canAddMore = currentCount < MAX_ATTACHMENTS && !disabled && !readOnly;
  const accept = GENERAL_ATTACHMENT_ACCEPT;

  // Nothing to show a viewer who cannot attach anything: an "Attachments (0/5)"
  // header over an empty list is noise in a form they are only reading. The
  // loading and error states still render - a failed fetch is not the same as
  // "no attachments", and swallowing it would hide the failure entirely.
  if (readOnly && currentCount === 0 && !isLoadingAttachments && !isAttachmentsError) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div ref={headerRef} className="flex items-center justify-between">
        <span className="text-sm font-medium text-secondary">
          Attachments{' '}
          <span className="font-normal text-text-muted">
            {readOnly ? `(${currentCount})` : `(${currentCount}/${MAX_ATTACHMENTS})`}
          </span>
        </span>
        {canAddMore && (
          <TextButton onClick={() => fileInputRef.current?.click()} className="text-xs">
            + Add file
          </TextButton>
        )}
      </div>

      {/* Drop zone - only shown when more files can be added */}
      {canAddMore && (
        <div
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={[
            'relative flex items-center justify-center rounded-lg border border-dashed px-4 py-5 transition-colors',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border bg-bg-subtle hover:border-primary/50',
          ].join(' ')}
        >
          {isDragging && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-primary/90"
            >
              <UploadCloudIcon className="h-8 w-8 text-white" />
              <p className="mt-1.5 text-sm font-medium text-white">Drop to attach</p>
            </div>
          )}
          <div className="flex flex-col items-center gap-1 text-center">
            <PaperclipIcon className="h-5 w-5 text-text-muted" />
            <p className="text-xs text-text-muted">
              <TextButton onClick={() => fileInputRef.current?.click()} tone="sentence">
                Click to attach
              </TextButton>
              {' '}or drag and drop
            </p>
            <p className="text-xs text-text-muted">{GENERAL_ATTACHMENT_SUMMARY_LABEL} · max 10 MB each</p>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={handleFileInputChange}
        disabled={disabled}
      />

      {/* Error state for edit mode attachment fetch */}
      {isEditMode && isAttachmentsError && (
        <ReadError message="Couldn't load attachments" onRetry={() => void refetchAttachments()} />
      )}

      {/* Existing attachments list (edit mode) */}
      {isEditMode && (
        <Reveal group={reveal} read={attachmentsRead} placeholder={<AttachmentsSkeleton />}>
          {existingAttachments && existingAttachments.length > 0 && (
            <ul ref={listRef} className="space-y-1.5" aria-label="Attached files">
              {existingAttachments.map((attachment, index) => (
                <SupportingDocumentRow
                  key={attachment.id}
                  document={attachment}
                  onOpen={() => downloadActivityDocument(resolvedAccountId, activityId!, attachment.id)}
                  onRemoveRequested={readOnly ? undefined : () => {
                    removedRef.current = { id: attachment.id, index };
                    setPendingRemoval(attachment);
                  }}
                />
              ))}
            </ul>
          )}
        </Reveal>
      )}

      {isEditMode && (
        <RemoveDocumentDialog
          document={pendingRemoval}
          record="time entry"
          remove={(documentId) => deleteActivityDocument(resolvedAccountId, activityId!, documentId)}
          onRemoved={refreshAttachments}
          onClose={() => setPendingRemoval(null)}
        />
      )}

      {/* Pending files list (create mode) */}
      {!isEditMode && pendingFiles.length > 0 && (
        <ul className="space-y-1.5" aria-label="Files to attach">
          {pendingFiles.map((pending) => (
            <PendingFileRow
              key={pending.id}
              pending={pending}
              onRemove={removePending}
            />
          ))}
        </ul>
      )}

      {/* Upload in-progress indicator (edit mode) */}
      {isEditMode && uploadMutation.isPending && (
        <p className="text-xs text-text-muted" aria-live="polite">
          Uploading…
        </p>
      )}
    </div>
  );
}

/** One attachment row (`px-3 py-2`, bordered) at its loaded height. */
function AttachmentsSkeleton() {
  return (
    <SkeletonRegion label="Loading attachments">
      <Skeleton className="h-[38px] w-full rounded-md" />
    </SkeletonRegion>
  );
}
